/**
 * Theme registry — single source of which themes exist.
 *
 * Phase 2 of docs/backlog/theming-system.md. Today there is exactly ONE theme
 * ("dark"), whose colour VALUES live unconditionally in src/styles/index.css
 * (@theme + :root). So "dark" needs no `[data-theme="dark"]` override block — it
 * IS the default cascade. The registry exists so the switch machinery is in place:
 * adding a second theme later = one entry here + a `[data-theme="<id>"]` block in
 * CSS overriding the --color-* source tokens. No other code changes.
 *
 * NB: `data-theme="paper"` (the HUD scope) is element-scoped, NOT a top-level
 * theme — it is not part of this registry and must not be added here.
 */

export type ThemeId = 'dark';

export interface ThemeMeta {
  id: ThemeId;
  /** i18n key for the human-facing label (used by the Phase 3 selector). */
  labelKey: string;
}

export const THEMES: Record<ThemeId, ThemeMeta> = {
  dark: { id: 'dark', labelKey: 'settings.theme.dark' },
};

export const DEFAULT_THEME: ThemeId = 'dark';

export const THEME_IDS = Object.keys(THEMES) as ThemeId[];

/** True once a real choice exists — the selector stays hidden until then (Phase 3). */
export const HAS_MULTIPLE_THEMES = THEME_IDS.length > 1;

export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === 'string' && value in THEMES;
}
